/* Write-a-review page (sections/pw-review-form.liquid): builds one card per product from the URL, sends each
   card to the Patch & Willow Reviews worker. Photos are resized in the browser to 1600 px JPEG. */
(() => {
  const root = document.querySelector('[data-pw-review-form]');
  if (!root) return;
  const api = root.dataset.api;
  const params = new URLSearchParams(location.search);
  const token = params.get('t');
  const cardsEl = root.querySelector('[data-pw-cards]');
  const template = root.querySelector('[data-pw-card]');
  let uid = 0;

  async function resized(file) {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);
      canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
      return new File([blob], 'photo.jpg', { type: 'image/jpeg' });
    } catch {
      return file;
    }
  }

  function card({ scope, product = null, firstName = '' }) {
    const form = template.content.firstElementChild.cloneNode(true);
    const id = ++uid;
    form.querySelectorAll('[id^="r"]').forEach((input) => {
      const label = form.querySelector(`label[for="${input.id}"]`);
      input.id = `${input.id}-${id}`;
      label.htmlFor = input.id;
    });
    form.dataset.scope = scope;
    if (product) {
      form.dataset.product = product.handle;
      const box = form.querySelector('[data-product]');
      box.hidden = false;
      box.querySelector('[data-product-title]').textContent = product.title;
      if (product.image) box.querySelector('img').src = product.image;
      else box.querySelector('img').remove();
    } else {
      form.querySelector('[data-store-label]').hidden = false;
      form.elements.body.placeholder = 'How were ordering, shipping and our support?';
    }
    if (firstName) form.elements.author.value = firstName;
    form.photos = [];
    return form;
  }

  async function productInfo(handle) {
    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}.js`);
      if (!res.ok) return null;
      const p = await res.json();
      return { handle, title: p.title, image: p.featured_image ? `${p.featured_image.replace(/^\/\//, 'https://')}${p.featured_image.includes('?') ? '&' : '?'}width=160` : null };
    } catch {
      return null;
    }
  }

  function renderPhotos(form) {
    const box = form.querySelector('[data-photos]');
    box.innerHTML = '';
    form.photos.forEach((file, i) => {
      const item = document.createElement('span');
      item.className = 'pw-rform__photo';
      item.innerHTML = `<img alt="" src="${URL.createObjectURL(file)}"><button type="button" aria-label="Remove photo" data-remove="${i}">×</button>`;
      box.append(item);
    });
    form.querySelector('.pw-rform__add').hidden = form.photos.length >= 6;
  }

  async function submit(form) {
    const error = form.querySelector('[data-error]');
    const fail = (message) => {
      error.textContent = message;
      error.hidden = false;
    };
    error.hidden = true;
    const rating = form.querySelector('input[name="rating"]:checked');
    if (!rating) return fail('Please choose a star rating.');
    if (!form.elements.body.value.trim()) return fail('Please write a few words about it.');
    if (!form.elements.author.value.trim()) return fail('Please add the name to show with your review.');
    const button = form.querySelector('[type="submit"]');
    button.disabled = true;
    button.textContent = 'Sending…';
    const data = new FormData();
    if (token) data.append('t', token);
    data.append('scope', form.dataset.scope);
    if (form.dataset.product) data.append('product', form.dataset.product);
    data.append('rating', rating.value);
    for (const key of ['title', 'body', 'author', 'location', 'website']) data.append(key, form.elements[key].value);
    for (const file of form.photos) data.append('photos', await resized(file));
    try {
      const res = await fetch(`${api}/api/public/reviews`, { method: 'POST', body: data });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(out.error || 'Something went wrong — please try again.');
      form.innerHTML = '<p class="pw-rform__thanks"><strong>Thank you!</strong> We’ll post your review after a quick check.</p>';
      document.dispatchEvent(new CustomEvent('pw:review', { detail: { action: 'submitted', scope: form.dataset.scope, verified: Boolean(token), photos: form.photos.length, rating: Number(rating.value) } }));
    } catch (e) {
      button.disabled = false;
      button.textContent = 'Submit review';
      fail(e.message);
    }
  }

  root.addEventListener('change', (event) => {
    const input = event.target.closest('[data-photo-input]');
    if (!input) return;
    const form = input.closest('form');
    form.photos = [...form.photos, ...[...input.files].filter((f) => f.type.startsWith('image/'))].slice(0, 6);
    input.value = '';
    renderPhotos(form);
  });
  root.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-remove]');
    if (!remove) return;
    const form = remove.closest('form');
    form.photos.splice(Number(remove.dataset.remove), 1);
    renderPhotos(form);
  });
  root.addEventListener('submit', (event) => {
    event.preventDefault();
    submit(event.target);
  });

  (async () => {
    const forms = [];
    let firstName = '';
    if (token) {
      try {
        const res = await fetch(`${api}/api/public/link?t=${encodeURIComponent(token)}`);
        const link = await res.json();
        if (!res.ok) throw new Error(link.error);
        firstName = link.firstName || '';
        if (firstName) root.querySelector('[data-pw-intro]').textContent = `Hi ${firstName}, how did your order turn out? Review one item or all of them.`;
        const focus = params.get('p');
        const products = (await Promise.all(link.products.map((p) => productInfo(p.handle)))).filter(Boolean);
        products.sort((a, b) => (b.handle === focus) - (a.handle === focus));
        products.forEach((product) => forms.push(card({ scope: 'product', product, firstName })));
      } catch (e) {
        root.querySelector('[data-pw-intro]').textContent = 'This review link has expired, but you can still leave a review below.';
      }
    }
    if (!forms.length && params.get('product')) {
      const product = await productInfo(params.get('product'));
      if (product) forms.push(card({ scope: 'product', product }));
    }
    forms.push(card({ scope: 'store', firstName }));
    cardsEl.replaceChildren(...forms);
    // a star tapped in the review email arrives as ?rating=N for the first card
    const rating = Number(params.get('rating'));
    if (rating >= 1 && rating <= 5) forms[0].querySelector(`input[name="rating"][value="${rating}"]`).checked = true;
  })();
})();
